import { RouteRecordRaw } from 'vue-router'
import authRoutes from './auth'
import authGuard from '@/auth/AuthGuard'
import accountDisabledGuard from './accountDisabledGuard'
import subscriptionGuard from './subscriptionGuard'
import v1SubscriptionGuard from './v1SubscriptionGuard'
import mobileOnboardingGuard from './mobileOnboardingGuard'
import { isEBEnv } from '@/helpers/env'

const sandboxGuard = () => {
  if (!isEBEnv('local')) {
    return { name: 'app' }
  }
  return true
}

export const mobileRoutes: Array<RouteRecordRaw> = [
  ...authRoutes,
  {
    path: '/health',
    name: 'health',
    component: () => import('../views/_Health.vue'),
  },
  {
    path: '/sandbox',
    name: 'sandbox',
    beforeEnter: sandboxGuard,
    component: () => import('../views/sandbox/ColorSandbox.vue'),
  },
  {
    path: '/welcome',
    name: 'welcome',
    component: () => import('../features/onboarding/views/OnboardingView.vue'),
  },
  {
    path: '/login',
    name: 'login',
    beforeEnter: [mobileOnboardingGuard],
    component: () => import('../features/onboarding/views/Login.vue'),
  },
  {
    path: '/',
    name: 'app',
    beforeEnter: [authGuard, accountDisabledGuard],
    component: () => import('../views/MainView.vue'),
    children: [
      {
        path: 'bank-callback',
        name: 'bankCallback',
        component: () => import('../views/BankCallbackView.vue'),
      },
      {
        path: 'subscription-expired',
        name: 'subscriptionExpired',
        component: () => import('../views/SubscriptionExpired.vue'),
      },
      {
        path: 'subscription-new',
        name: 'subscriptionNew',
        component: () =>
          import(
            '../features/subscription/views/SubscriptionNewViewMobile.vue'
          ),
      },
      {
        path: 'subscription-extended',
        name: 'subscriptionExtended',
        component: () => import('../views/SubscriptionExtended.vue'),
      },
      {
        path: 'special-offer',
        component: () => import('../layouts/LayoutMobileDialog.vue'),
        children: [
          {
            path: '',
            name: 'specialOffer',
            components: {
              main: () => import('../views/mobile/SpecialOfferView.vue'),
            },
          },
        ],
      },
      {
        path: 'settings',
        component: () => import('../layouts/LayoutMobileDialog.vue'),
        children: [
          {
            path: 'subscription',
            name: 'settingsSubscription',
            beforeEnter: v1SubscriptionGuard,
            meta: { header: 'Subskrypcja' },
            components: {
              main: () => import('../views/SettingsSubscriptionViewMobile.vue'),
            },
          },
          {
            path: 'reminder',
            name: 'settingsReminder',
            meta: { header: 'Przypomnienia' },
            components: {
              main: () => import('../views/SettingsReminderViewMobile.vue'),
            },
          },
          {
            path: 'notifications',
            name: 'notifications',
            components: {
              main: () =>
                import(
                  '../features/notifications/views/mobile/NotificationsView.vue'
                ),
            },
          },
        ],
      },
      {
        path: 'budget',
        beforeEnter: subscriptionGuard,
        component: () => import('../layouts/LayoutMobile.vue'),
        children: [
          {
            path: ':budgetId/settings',
            name: 'budgetSettings',
            components: {
              main: () => import('../views/BudgetSettingsViewMobile.vue'),
            },
          },
          {
            path: ':budgetId/statistics',
            name: 'statistics',
            components: {
              main: () => import('../views/StatisticsViewMobile.vue'),
            },
          },
          {
            path: ':budgetId/period/:periodKey',
            alias: [':budgetId'],
            name: 'budget',
            components: {
              main: () => import('../views/BudgetViewContainerMobile.vue'),
            },
          },
          {
            path: ':budgetId/period/:periodKey/bankAccounts',
            name: 'bankAccounts',
            components: {
              main: () => import('../views/mobile/BankAccountsView.vue'),
            },
          },
          {
            path: ':budgetId/period/:periodKey/bankAccounts/:bankAccountId',
            name: 'bankAccount',
            components: {
              main: () => import('../views/mobile/BankAccountView.vue'),
            },
          },
          {
            path: ':budgetId/transactions',
            name: 'transactions',
            components: {
              main: () => import('../views/mobile/TransactionsView.vue'),
            },
          },
          {
            path: ':budgetId/more',
            name: 'more',
            components: {
              main: () => import('../views/mobile/MoreView.vue'),
            },
          },
          {
            path: ':budgetId/bank-connections',
            name: 'bankConnections',
            components: {
              main: () => import('../views/mobile/BankConnectionsView.vue'),
            },
          },
          {
            path: ':budgetId/bank-connection/:bankConnectionId',
            name: 'bankConnectionOnboardingStatus',
            components: {
              main: () =>
                import(
                  '../views/mobile/BankConnectionOnboardingStatusView.vue'
                ),
            },
          },
        ],
      },
      {
        path: 'budget',
        beforeEnter: subscriptionGuard,
        component: () => import('../layouts/LayoutMobileDialog.vue'),
        children: [
          {
            path: ':budgetId/period/:periodKey/assign',
            name: 'budgetAssign',
            components: {
              main: () => import('../views/BudgetAssignViewMobile.vue'),
            },
          },
          {
            path: ':budgetId/period/:periodKey/auto-assign',
            name: 'autoAssign',
            components: {
              main: () => import('../views/mobile/AutoAssignView.vue'),
            },
          },
          {
            path: ':budgetId/period/:periodKey/assignment-history',
            name: 'assignmentHistory',
            components: {
              main: () => import('../views/mobile/AssignmentHistoryView.vue'),
            },
          },
          {
            path: ':budgetId/period/:periodKey/categories/:categoryId',
            name: 'budgetCategory',
            components: {
              main: () => import('../views/CategorySideviewMobile.vue'),
            },
          },
          {
            path: ':budgetId/period/:periodKey/categories/:categoryId/move-money',
            name: 'moveMoney',
            components: {
              main: () => import('../views/mobile/MoveMoneyView.vue'),
            },
          },
          {
            path: ':budgetId/period/:periodKey/categories/:categoryId/move',
            name: 'moveCategory',
            components: {
              main: () => import('../views/mobile/MoveCategoryView.vue'),
            },
          },
          {
            path: ':budgetId/period/:periodKey/categories/:categoryId/target',
            name: 'targetView',
            components: {
              main: () => import('../views/mobile/TargetView.vue'),
            },
          },
          {
            path: ':budgetId/period/:periodKey/categories/:categoryId/move-transactions',
            name: 'moveTransactions',
            components: {
              main: () => import('../views/mobile/MoveTransactionsView.vue'),
            },
          },
          {
            path: ':budgetId/period/:periodKey/categories/:categoryId/cover',
            name: 'coverOverspending',
            components: {
              main: () => import('../views/mobile/CoverOverspendingView.vue'),
            },
          },
          {
            path: ':budgetId/period/:periodKey/categories/:categoryId?/transactions/:transactionId?',
            name: 'budgetCategoryTransaction',
            components: {
              main: () => import('../views/TransactionViewMobile.vue'),
            },
          },
          {
            path: ':budgetId/period/:periodKey/categories/:categoryId?/templates/:transactionTemplateId?',
            name: 'transactionTemplate',
            components: {
              main: () => import('../views/mobile/TransactionTemplateView.vue'),
            },
          },
          {
            path: ':budgetId/period/:periodKey/categories/:categoryId?/templates/:transactionTemplateId/date/:templateDate/confirm',
            name: 'transactionDraft',
            components: {
              main: () => import('../views/mobile/TransactionDraftView.vue'),
            },
          },
          {
            path: ':budgetId/group/:categoryGroupId/category/:categoryId',
            name: 'budgetCategoryMobile',
            meta: { header: 'Edytuj kategorię' },
            components: {
              main: () => import('../views/CategoryViewMobile.vue'),
            },
            props: {
              default: true,
              mobile: (route) => ({
                onDeleteBackValue: route.params.onDeleteBackValue,
              }),
            },
          },
          {
            path: ':budgetId/group/:categoryGroupId/category/:categoryId',
            name: 'budgetCategoryDeleteMobile',
            components: {
              main: () => import('../views/CategoryDeleteViewMobile.vue'),
            },
          },
          {
            path: ':budgetId/group/:categoryGroupId',
            name: 'budgetCategoryAddMobile',
            meta: { header: 'Dodaj kategorię' },
            components: {
              main: () => import('../views/CategoryViewMobile.vue'),
            },
          },
          {
            path: ':budgetId/category-group/:categoryGroupId?',
            name: 'budgetCategoryGroupMobile',
            components: {
              main: () => import('../views/CategoryGroupViewMobile.vue'),
            },
          },
          {
            path: ':budgetId/category-group/:categoryGroupId/delete',
            name: 'budgetCategoryGroupDeleteMobile',
            components: {
              main: () => import('../views/CategoryGroupDeleteViewMobile.vue'),
            },
          },
          {
            path: ':budgetId/:periodKey/transactions',
            name: 'budgetTransaction',
            components: {
              main: () => import('../views/TransactionViewMobile.vue'),
            },
          },
          {
            path: ':budgetId?/edit',
            name: 'budgetModal',
            components: {
              main: () => import('../views/BudgetModalViewMobile.vue'),
            },
          },
          {
            path: ':budgetId/invite',
            name: 'budgetInvite',
            components: {
              main: () => import('../views/BudgetInviteViewMobile.vue'),
            },
          },
          {
            path: ':budgetId?/list',
            name: 'budgetsList',
            meta: { header: 'Twoje budżety' },
            components: {
              main: () => import('../views/SettingsBudgetListViewMobile.vue'),
            },
          },
          {
            path: ':budgetId?/import',
            name: 'importTransactions',
            meta: { header: 'Import Transakcji' },
            components: {
              main: () => import('../views/mobile/ImportTransactionsView.vue'),
            },
          },
          {
            path: ':budgetId/loans/:loanId?',
            name: 'loans',
            components: {
              main: () =>
                import('../features/loans/views/mobile/LoanView/LoanView.vue'),
            },
          },
          {
            path: ':budgetId/transaction-sides',
            name: 'transactionSides',
            components: {
              main: () =>
                import(
                  '../features/transaction-sides/views/mobile/TransactionSidesView/TransactionSidesView.vue'
                ),
            },
          },
          {
            path: ':budgetId/transaction-sides/create',
            name: 'transactionSideCreate',
            components: {
              main: () =>
                import(
                  '../features/transaction-sides/views/mobile/TransactionSideModalView/TransactionSideModalView.vue'
                ),
            },
          },
          {
            path: ':budgetId/transaction-sides/:transactionSideId/edit',
            name: 'transactionSideEdit',
            components: {
              main: () =>
                import(
                  '../features/transaction-sides/views/mobile/TransactionSideModalView/TransactionSideModalView.vue'
                ),
            },
          },
          {
            path: ':budgetId/transaction-sides/merge',
            name: 'transactionSidesMerge',
            components: {
              main: () =>
                import(
                  '../features/transaction-sides/views/mobile/MergeTransactionSidesModalView/MergeTransactionSidesModalView.vue'
                ),
            },
          },
          {
            path: ':budgetId/transaction-sides/delete',
            name: 'transactionSidesDelete',
            components: {
              main: () =>
                import(
                  '../features/transaction-sides/views/mobile/DeleteTransactionSidesModalView/DeleteTransactionSidesModalView.vue'
                ),
            },
          },
          {
            path: ':budgetId/bankAccount/:bankAccountId?/modal',
            name: 'bankAccountEdit',
            components: {
              main: () => import('../views/mobile/BankAccountModalView.vue'),
            },
          },
          {
            path: ':budgetId/bankAccount/:bankAccountId?/close-modal',
            name: 'bankAccountClose',
            components: {
              main: () =>
                import('../views/mobile/BankAccountCloseModalView.vue'),
            },
          },
          {
            path: ':budgetId/bankAccount/:bankAccountId/reconcile',
            name: 'bankAccountReconcile',
            components: {
              main: () =>
                import('../views/mobile/BankAccountReconcileView.vue'),
            },
          },
          {
            path: ':budgetId/bankAccount/:bankAccountId/reconcile-edit/:transactionId',
            name: 'bankAccountReconcileEdit',
            components: {
              main: () =>
                import('../views/mobile/BankAccountReconcileEditView.vue'),
            },
          },
          {
            path: ':budgetId/bankAccount/:bankAccountId/transfer/:transactionId?',
            name: 'bankAccountTransfer',
            components: {
              main: () => import('../views/mobile/BankAccountTransferView.vue'),
            },
          },
          {
            path: ':budgetId/bankAccount/:bankAccountId/link-connection',
            name: 'bankAccountLinkConnection',
            components: {
              main: () =>
                import('../views/mobile/BankAccountLinkConnectionView.vue'),
            },
          },
          {
            path: ':budgetId/bank-connections/initiate',
            name: 'bankConnectionInitiate',
            components: {
              main: () =>
                import('../views/mobile/BankConnectionInitiateView.vue'),
            },
          },
          {
            path: ':budgetId/bank-connections/:bankConnectionId/rename',
            name: 'bankConnectionRename',
            components: {
              main: () =>
                import('../views/mobile/BankConnectionRenameView.vue'),
            },
          },
          {
            path: ':budgetId/bank-connections/:bankConnectionId/reauthorize',
            name: 'bankConnectionReauthorize',
            components: {
              main: () =>
                import('../views/mobile/BankConnectionReauthorizeView.vue'),
            },
          },
          {
            path: ':budgetId/bank-connections/link/:bankConnectionId/:bankConnectionAccountId',
            name: 'bankConnectionLinkAccount',
            components: {
              main: () =>
                import('../views/mobile/BankConnectionLinkAccountView.vue'),
            },
          },
        ],
      },
    ],
  },
]
